/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, memo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useFlexCredentialByTypeQuery } from '../../../../redux/flex-professional-credentials';
import LoadingSpinner from '../../../../components/spinner/LoadingSpinner';
import checkIcon from '../../../../assets/images/check.svg';
import redCloseIcon from '../../../../assets/images/close-red.svg';

const processStatus = (status) => {
	if (!status) {
		return 'pending';
	}
	if (status.startsWith('is_')) {
		return status.replace('is_', '').replaceAll('_', ' ');
	}
	return status.replaceAll('_', ' ').toLowerCase();
};

const mapCredentialStatus = (credential, parentCredentialName) => ({
	...credential,
	data: credential.data.map((row) => ({
		...row,
		status: processStatus(row.status),
		status_code: row.status,
		parent_credential_name: parentCredentialName
	}))
});

const ProfessionalCredentialsData = ({
	caregiverId,
	managementCompanyId,
	rowData,
	enabledCredentials,
	ActionsColumnComponent = null
}) => {
	const [rows, setRows] = useState({});
	const { data: credentialData, isFetching } = useFlexCredentialByTypeQuery(
		{
			caregiverId,
			credType: rowData.credential_type,
			managementCompanyId,
			source: 'table'
		},
		{
			refetchOnMountOrArgChange: true
		}
	);

	const loadingTemplate = () => {
		return <LoadingSpinner className="saving-spinner" />;
	};

	const defaultTemplate = (item, columnData) => {
		const { field } = columnData;

		if (typeof item[field] === 'boolean') {
			return <>{item[field] ? <img src={checkIcon} alt="success" /> : <img src={redCloseIcon} alt="failed" />}</>;
		}

		return <span>{item[field] !== null && item[field] !== undefined ? item[field] : '-'}</span>;
	};

	const getActionColumnTemplate = useCallback(
		(credentialCode, column) => (item) => {
			if (!ActionsColumnComponent) {
				return null;
			}
			const sameCredentialCount = credentialData?.data?.length || 0;
			return (
				<ActionsColumnComponent
					column={column}
					credentialCode={credentialCode}
					rowData={item}
					sameCredentialCount={sameCredentialCount}
					enabledCredentials={enabledCredentials}
				/>
			);
		},
		[credentialData]
	);

	const getListTemplate = useCallback(
		(credentialCode) => (item, columnData) => {
			const { field } = columnData;
			const list = item[field];

			if (list.length <= 1) {
				return <span>{list[0]?.name ?? '-'}</span>;
			}

			return (
				<ul style={{ marginBottom: 0, paddingLeft: '16px' }}>
					{item[field].map((listItem, idx) => (
						// eslint-disable-next-line react/no-array-index-key
						<li key={`list-item-${credentialCode}-${item.id}-${field}-${idx}`}>{listItem.name}</li>
					))}
				</ul>
			);
		},
		[]
	);

	useEffect(() => {
		if (credentialData && rowData.credential_type) {
			setRows((prev) => ({
				...prev,
				[rowData.credential_type]: mapCredentialStatus(credentialData, rowData.name)
			}));
		}
	}, [credentialData, rowData.credential_type]);

	const columns = () => {
		if (isFetching) {
			return <Column key="loader" field="loading" body={loadingTemplate} />;
		}
		return rows[rowData.credential_type]?.columns?.map((column) => {
			if (column.renderer && column.renderer === 'edit') {
				return (
					<Column
						key="actions"
						field="actions"
						header="Actions"
						alignFrozen="right"
						body={getActionColumnTemplate(rowData.credential_type, column)}
						style={{ width: '110px' }}
					/>
				);
			}

			if (column?.renderer === 'list_renderer') {
				return (
					<Column
						key={column?.data}
						field={column?.data}
						sortable
						header={column?.title}
						body={(item, columnData) => getListTemplate(rowData.credential_type)(item, columnData)}
					/>
				);
			}

			return (
				<Column
					key={column?.data}
					field={column?.data}
					sortable
					header={column?.title}
					style={{ padding: '1rem 1.6rem' }}
					className={column?.data === 'status' ? 'status-column' : ''}
					body={defaultTemplate}
				/>
			);
		});
	};

	if (!isFetching && !rowData.submitted.length) {
		return <></>;
	}

	return (
		<DataTable
			loading={isFetching || !Object.keys(rows).length}
			value={rows[rowData.credential_type]?.data}
			className="credential-data"
			removableSort
			loadingTemplate={loadingTemplate}
		>
			{columns()}
		</DataTable>
	);
};

export default memo(ProfessionalCredentialsData);
